import React from "react";
import { AlertTriangle, AlertCircle, Info } from "lucide-react";

export interface AlertProps {
  variant: "info" | "warning" | "error";
  size?: "default" | "small";
  title?: string;
  description?: string;
  actionLabel?: string;
  onActionClick?: () => void;
  className?: string;
}

const sizeStyles = {
  default: {
    container: "px-6 py-4",
    gap: "gap-3",
    twoLineGap: "gap-6",
    title: "font-medium text-base leading-relaxed sm:text-lg sm:leading-relaxed",
    description: "text-[13px] leading-relaxed sm:text-[15px] sm:leading-loose",
    singleLine: "text-base leading-relaxed sm:text-lg sm:leading-relaxed",
    action: "text-base leading-relaxed sm:text-lg sm:leading-relaxed",
    iconSize: 20,
  },
  small: {
    container: "px-4 py-3",
    gap: "gap-2",
    twoLineGap: "gap-4",
    title: "font-semibold text-[11px] leading-snug sm:text-[13px] sm:leading-relaxed",
    description: "text-[11px] leading-snug sm:text-[13px] sm:leading-relaxed",
    singleLine: "text-[11px] leading-snug sm:text-[13px] sm:leading-relaxed",
    action: "text-[11px] leading-snug sm:text-[13px] sm:leading-relaxed",
    iconSize: 16,
  },
};

const variantStyles = {
  info: {
    container: "bg-bg2 text-fg",
    icon: "text-fg2",
    bg: undefined as string | undefined,
    IconComponent: Info,
  },
  warning: {
    container: "text-fg",
    icon: "text-fg",
    bg: "rgba(235, 143, 43, 0.15)",
    IconComponent: AlertTriangle,
  },
  error: {
    container: "text-fg",
    icon: "text-fg",
    bg: "rgba(229, 61, 0, 0.15)",
    IconComponent: AlertCircle,
  },
};

export const Alert = ({
  variant,
  size = "default",
  title,
  description,
  actionLabel,
  onActionClick,
  className = "",
}: AlertProps) => {
  const styles = variantStyles[variant];
  const sizing = sizeStyles[size];
  const IconComponent = styles.IconComponent;
  const isTwoLine = title && description;

  return (
    <div
      role="alert"
      className={`flex items-center rounded-lg ${sizing.container} ${isTwoLine ? sizing.twoLineGap : sizing.gap} ${styles.container} ${className}`}
      style={styles.bg ? { backgroundColor: styles.bg } : undefined}
    >
      <IconComponent className={`flex-shrink-0 ${styles.icon}`} size={sizing.iconSize} />

      <div className="flex-1 min-w-0">
        {isTwoLine ? (
          <>
            <div className={sizing.title}>{title}</div>
            <div className={`${sizing.description} mt-0.5`}>{description}</div>
          </>
        ) : (
          <div className={sizing.singleLine}>{title || description}</div>
        )}
      </div>

      {actionLabel && (
        <button
          onClick={onActionClick}
          className={`flex-shrink-0 underline hover:opacity-80 transition-opacity ${sizing.action}`}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
};
